import { SwarovskiOptikSegmentShort } from "@swo/shared";
import { SwarovskiOptikLocaleFrontend } from "@swo/storefront";
import { useCallback, useState } from "react";
import { HeaderPinnedState } from "../../components/HeaderComponent/subcomponents/use-header-pinned-state";
import { useT2 } from "../../domain/translations/translations";
import { useGeneralTranslationsContext } from "../../global/hooks/use-general-translations-context";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { PDPBelowTheFoldLayout } from "./PDPBelowTheFoldLayout";
import { PDPMetadata } from "./PDPMetadata";
import { PDPModal } from "./PDPModal";
import { ProductDetailPageDescription } from "./PDP_Description/ProductDetailPageDescription";
import { usePdpGalleryWithLightbox } from "./PDP_Lightbox/use-pdp-gallery-with-lightbox";
import { PDPAboveTheFoldLayout } from "./PDP_SingleImageCarousel/PDPAboveTheFoldLayout";
import { usePDPAvailabilityBarState } from "./PDP_SingleImageCarousel/availability/pdp-availability";
import { useProductVariantState } from "./PDP_SingleImageCarousel/availability/use-product-variant-state";
import { usePDPGiftCardProps } from "./PDP_SingleImageCarousel/gift-card/usePDPGiftCardProps";
import { getPdpStickyDesktopButton } from "./PDP_SingleImageCarousel/price-bar/GetPdpStickyDesktopButton";
import { getPDPActions } from "./getPDPActions";
import { PageProps } from "./product-detail-page-layout";
import { outOfStock } from "./state/get-product-availability";
import { useProductView } from "./use-product-view";
import { getGalleryProps } from "./PDP_SingleImageCarousel/PDPStickyGallery";

export type PDPMainComponentProps = {
  data: Omit<PageProps["data"], "header" | "footer">;
  page: PageProps["page"];
  headerState: HeaderPinnedState;
  locale: SwarovskiOptikLocaleFrontend;
  segment: SwarovskiOptikSegmentShort;
  PdpInformationSet: React.ReactNode;
  children: React.ReactNode;
};

export const PDPMainComponent: React.FC<PDPMainComponentProps> = props => {
  const [isModalOpen, setModalOpen] = useState(false);
  const hideModalCallback = useCallback(() => {
    setModalOpen(false);
  }, []);

  const gt = useGeneralTranslationsContext();
  const reviewsT = useT2(props.data.translations.reviews);
  const pdpT = useT2(props.data.translations.pdp);

  const [pdpState, dispatch] = useProductVariantState(props.data.selectedProduct, props.page, props.data.availability);

  const giftCardData = usePDPGiftCardProps(props.data.translations.pdp, pdpState.selectedProduct.isGiftCard);

  const { addToCart: addToCart_, areCartActionsLocked } = getPDPActions(
    setModalOpen,
    pdpState.selectedProduct.id,
    giftCardData?.payload,
    props.data.links,
  );

  const addToCard = () => addToCart_({ tag: "pdp", selectedVariant: pdpState.selectedVariant });

  const galleryConfig = getGalleryProps(pdpState.selectedProduct, pdpState.selectedVariant, 10);
  const galleryWithLightbox = usePdpGalleryWithLightbox(galleryConfig);

  const availabilityState = usePDPAvailabilityBarState(
    pdpState.selectedVariant,
    pdpState.computed.availability ??
      outOfStock() /* FIXME: This is a workaround for the fact that the availability may not be available (ie. for non-country locales)*/,
    galleryWithLightbox?.galleryConfig.primaryImage,
    addToCard,
    props.locale,
    props.segment,
    props.data.translations.pdp,
    props.data.links,
  );

  const primaryButton = getPdpStickyDesktopButton(availabilityState.action, areCartActionsLocked);

  useProductView(
    pdpState.selectedProduct,
    pdpState.selectedVariant,
    props.data.taxonomy.productCategoryKey,
    props.locale,
    props.segment,
  );

  return (
    <>
      <PDPMetadata
        selectedProduct={pdpState.selectedProduct}
        selectedVariant={pdpState.selectedVariant}
        seoMetadata={props.data.seoMetadata}
        taxonomy={props.data.taxonomy}
        availability={props.data.availability}
        page={props.page}
      />

      <PDPModal isModalOpen={isModalOpen} hideModalCallback={hideModalCallback} />

      <PDPAboveTheFoldLayout
        relatedBundles={props.data.related.bundles}
        PdpInformationSet={props.PdpInformationSet}
        reviewsT={reviewsT}
        pdpT={pdpT}
        taxonomy={props.data.taxonomy}
        pdpState={pdpState}
        dispatch={dispatch}
        galleryWithLightbox={galleryWithLightbox}
        giftCardProps={giftCardData?.giftCardProps}
        availabilityState={availabilityState}
        primaryButton={primaryButton("normal")}
        reviews={props.data.selectedProduct.reviews}
        DescriptionComponent={
          <ProductDetailPageDescription productShortDescription={pdpState.selectedProduct.shortDescription} />
        }
      />

      <PDPBelowTheFoldLayout
        pdpState={pdpState}
        data={props.data}
        gt={gt}
        reviewsT={reviewsT}
        primaryButton={primaryButton("small")}
        headerState={props.headerState}
      >
        {props.children}
      </PDPBelowTheFoldLayout>
    </>
  );
};
